import { motion } from "framer-motion";
import { Check, MessageCircle } from "lucide-react";
import { waLink } from "@/data/site";

const rows = [
  {
    brand: "DOWSIL",
    label: "Silicone Sealants",
    accent: "#38bdf8",
    packs: "Tubes · Sausages · Cartons (24s)",
    moq: "1 carton",
    model: "Slab pricing by carton volume",
  },
  {
    brand: "Chennai Express",
    label: "Urad Dhal",
    accent: "#facc15",
    packs: "1kg retail · 30kg bags",
    moq: "1 case / bag",
    model: "Daily market-linked wholesale rate",
  },
  {
    brand: "Wopper",
    label: "Laundry Chemicals",
    accent: "#e879f9",
    packs: "5kg · 25kg · 50kg cans",
    moq: "1 can / bag",
    model: "Tiered bulk & contract pricing",
  },
];

const perks = [
  "Genuine, brand-authorised stock",
  "Transparent quotes within 24 hours",
  "Volume discounts for regular buyers",
  "Dispatch across Tamil Nadu",
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      className="relative z-10 border-t border-white/10 py-28 lg:py-40"
      data-testid="pricing-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="mb-16 max-w-2xl">
          <p className="overline mb-6">Pricing & Supply</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[1]">
            Fair rates, quoted for your volume.
          </h2>
          <p className="mt-6 text-white/55 leading-relaxed">
            We price to the pack size, grade and quantity you need. Below is how
            each category is quoted — request a live rate and we&apos;ll respond fast.
          </p>
        </div>

        {/* Data table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="overflow-hidden rounded-2xl border border-white/10"
          data-testid="pricing-table"
        >
          <div className="hidden grid-cols-12 gap-4 border-b border-white/10 bg-white/[0.02] px-6 py-4 text-xs uppercase tracking-wider text-white/40 md:grid">
            <div className="col-span-3">Brand</div>
            <div className="col-span-4">Pack Sizes</div>
            <div className="col-span-2">Min. Order</div>
            <div className="col-span-3">Pricing Model</div>
          </div>
          {rows.map((r) => (
            <div
              key={r.brand}
              className="grid grid-cols-1 gap-2 border-b border-white/10 px-6 py-6 transition-colors duration-300 last:border-0 hover:bg-white/[0.03] md:grid-cols-12 md:items-center md:gap-4"
              data-testid={`pricing-row-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="col-span-3 flex items-center gap-3">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: r.accent }}
                />
                <div>
                  <div className="font-display text-lg font-medium tracking-tight">
                    {r.brand}
                  </div>
                  <div className="text-xs text-white/40">{r.label}</div>
                </div>
              </div>
              <div className="col-span-4 text-sm text-white/70">{r.packs}</div>
              <div className="col-span-2 text-sm text-white/70">{r.moq}</div>
              <div className="col-span-3 text-sm text-white/70">{r.model}</div>
            </div>
          ))}
        </motion.div>

        {/* Perks + CTA */}
        <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="glass rounded-2xl p-8 lg:col-span-2">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {perks.map((p) => (
                <div key={p} className="flex items-start gap-3">
                  <Check className="mt-0.5 h-4 w-4 flex-none text-white" />
                  <span className="text-sm text-white/70">{p}</span>
                </div>
              ))}
            </div>
          </div>
          <a
            href={waLink("Hello, I'd like a price list / quote from V.S.V. Agencies.")}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col justify-between rounded-2xl bg-white p-8 text-black transition-transform duration-300 hover:-translate-y-1"
            data-testid="pricing-cta-btn"
          >
            <MessageCircle className="h-7 w-7" />
            <div className="mt-8">
              <div className="font-display text-2xl font-medium tracking-tight">
                Get a live quote
              </div>
              <div className="mt-1 text-sm text-black/60">
                Message us on WhatsApp for today&apos;s rate.
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
